// @core/stores/billing/BillingBillingAccountStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { billingApi } from '@core/api';
import type {
  AttachLegalEntityToBillingAccountRequest,
  AttachLegalEntityToBillingAccountResponse,
  BillingAccount,
  CreateBillingAccountRequest,
  UpdateBillingAccountRequest,
} from '@core/types/billing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class BillingBillingAccountStore {
  // List state
  items: BillingAccount[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: BillingAccount | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, BillingAccount>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): BillingAccount | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: BillingAccount | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Attach legal entity to billing account
   */
  async AttachLegalEntityToBillingAccount(billingAccountId: string, data: AttachLegalEntityToBillingAccountRequest): Promise<AttachLegalEntityToBillingAccountResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.AttachLegalEntityToBillingAccount(billingAccountId, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create billing account
   */
  async CreateBillingAccount(data: CreateBillingAccountRequest): Promise<BillingAccount> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.CreateBillingAccount(data);
      const item = response.billing_account;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Get billing account by ID
   */
  async GetBillingAccount(id: string): Promise<BillingAccount> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await billingApi.GetBillingAccount(id);
      const item = response.billing_account;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List billing accounts
   */
  async ListBillingAccounts(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await billingApi.ListBillingAccounts(params);

      runInAction(() => {
        this.items = response.billing_accounts?.accounts ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update billing account
   */
  async UpdateBillingAccount(id: string, data: UpdateBillingAccountRequest): Promise<BillingAccount> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.UpdateBillingAccount(id, data);
      const item = response.billing_account;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const billingBillingAccountStore = new BillingBillingAccountStore();
